import { useEffect, useRef, useState } from 'react';

// Custom hook for scroll animations
export const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated wrapper component
export const AnimateOnScroll = ({ 
  children, 
  animation = 'fadeUp', 
  delay = 0,
  threshold = 0.1,
  className = ''
}) => {
  const [ref, isVisible] = useScrollAnimation(threshold);

  const animations = {
    fadeUp: {
      hidden: { opacity: 0, transform: 'translateY(30px)' },
      visible: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeIn: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    slideLeft: {
      hidden: { opacity: 0, transform: 'translateX(-30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    slideRight: {
      hidden: { opacity: 0, transform: 'translateX(30px)' },
      visible: { opacity: 1, transform: 'translateX(0)' }
    },
    scale: {
      hidden: { opacity: 0, transform: 'scale(0.95)' },
      visible: { opacity: 1, transform: 'scale(1)' }
    }
  };

  const currentAnimation = animations[animation] || animations.fadeUp;
  const style = {
    ...currentAnimation.hidden,
    transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
    ...(isVisible ? currentAnimation.visible : {})
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
};

// Stagger animation for lists
export const StaggerContainer = ({ 
  children, 
  staggerDelay = 0.1,
  animation = 'fadeUp',
  threshold = 0.1,
  className = ''
}) => {
  const [ref, isVisible] = useScrollAnimation(threshold);

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children) 
        ? children.map((child, index) => (
            <AnimateOnScroll 
              key={index} 
              animation={animation}
              delay={isVisible ? index * staggerDelay : 0}
              threshold={0}
            >
              {child}
            </AnimateOnScroll>
          ))
        : children
      }
    </div>
  );
};

export default AnimateOnScroll;

